import { debugLogEvent, isAnalyticsDebugEnabled } from './debug'
import { isBrowser } from './env'

/** Boot stages of the analytics client, in the order they run (§3 boot flow). */
export type BootStage =
  | 'validate'
  | 'transport'
  | 'consent-script'
  | 'ready'
  | 'identify'
  | 'avo'
  | 'ga-cookies'

export type BootStageOutcome = 'ok' | 'failed' | 'timeout' | 'skipped'

/**
 * Datadog RUM global, present on pages that initialize RUM. Typed locally —
 * augmenting Window here would clash with @susu/datadog's own declaration.
 */
type DatadogRumGlobal = {
  addAction?: (name: string, context?: Record<string, unknown>) => void
}

const outcomes: Partial<Record<BootStage, BootStageOutcome>> = {}

/** Snapshot of every stage outcome reported so far (test/debug introspection). */
export const getBootStageOutcomes = (): Partial<
  Record<BootStage, BootStageOutcome>
> => ({ ...outcomes })

/**
 * FC-183 §8 health signal: reports one boot stage's outcome to Datadog RUM so
 * a degraded boot (a throwing destination, a GA timeout, a skipped identify)
 * is observable rather than silent. Degrades to a no-op when RUM is absent —
 * a telemetry failure must never affect the boot itself.
 */
export const reportBootStage = (
  stage: BootStage,
  outcome: BootStageOutcome,
  detail?: Record<string, unknown>,
): void => {
  if (!isBrowser()) {
    return
  }

  outcomes[stage] = outcome

  if (isAnalyticsDebugEnabled()) {
    debugLogEvent('boot', stage, { outcome, ...detail })
  }

  try {
    const rum = (window as { DD_RUM?: DatadogRumGlobal }).DD_RUM
    rum?.addAction?.('analytics_boot_stage', {
      stage,
      outcome,
      ...detail,
    })
  } catch {
    // Telemetry is best-effort by design.
  }
}
